import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { KeyRound, Loader2, Eye, EyeOff, CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { clientApi } from "../lib/api";
import { toast } from "sonner";

/**
 * Password reset page reached from the email link:
 *   `/reset-password?token=<opaque-token>`
 *
 * On mount, verifies the token is still redeemable. If yes, shows a form
 * for the new password. If no, shows a friendly error with a link back to
 * the login screen.
 */
export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [status, setStatus] = useState("checking"); // checking | valid | invalid
  const [invalidReason, setInvalidReason] = useState("");
  const [nit, setNit] = useState("");
  const [emailHint, setEmailHint] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setStatus("invalid"); setInvalidReason("missing"); return; }
    clientApi.get(`/portal/password-reset/verify?token=${encodeURIComponent(token)}`)
      .then(({ data }) => {
        if (data.valid) {
          setStatus("valid");
          setNit(data.nit || "");
          setEmailHint(data.email_hint || "");
        } else {
          setStatus("invalid");
          setInvalidReason(data.reason || "invalid");
        }
      })
      .catch(() => { setStatus("invalid"); setInvalidReason("error"); });
  }, [token]);

  const valid = newPwd.length >= 8 && newPwd === confirmPwd;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      await clientApi.post("/portal/password-reset/confirm", { token, new_password: newPwd });
      setDone(true);
      toast.success("Contraseña actualizada. Ya puedes ingresar.");
      setTimeout(() => navigate("/"), 2500);
    } catch (err) {
      const msg = err?.response?.data?.detail || "No se pudo actualizar la contraseña";
      toast.error(typeof msg === "string" ? msg : "Error al actualizar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-cm-cream flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <img src="/cm-logo.jpeg" alt="CM Calidad y Servicio" className="h-16 w-16 rounded-full object-cover ring-2 ring-cm-red/40 mx-auto" />
          <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft font-medium mt-3">Portal B2B</p>
          <h1 className="font-serif text-3xl mt-1">CM Calidad y Servicio</h1>
        </div>

        <div className="bg-white border border-cm-border rounded-lg p-6 shadow-sm">
          {status === "checking" && (
            <div className="text-center py-10" data-testid="reset-checking">
              <Loader2 className="h-8 w-8 mx-auto animate-spin text-cm-ink-soft" />
              <p className="text-sm text-cm-ink-soft mt-3">Verificando enlace...</p>
            </div>
          )}

          {status === "invalid" && (
            <div className="text-center py-4" data-testid="reset-invalid">
              <AlertTriangle className="h-10 w-10 mx-auto text-amber-600" />
              <h2 className="font-serif text-xl mt-4">Enlace no válido</h2>
              <p className="text-sm text-cm-ink-soft mt-2">
                {invalidReason === "expired"
                  ? "Este enlace ya expiró (los enlaces duran 2 horas). Solicita uno nuevo."
                  : invalidReason === "used"
                    ? "Este enlace ya fue usado. Si no fuiste tú, contacta a CM inmediatamente."
                    : invalidReason === "malformed"
                      ? "El enlace parece estar cortado o incompleto. Copia todo el enlace desde el correo o solicita uno nuevo."
                      : invalidReason === "not_found"
                        ? "No encontramos este enlace. Puede que sea de un correo anterior o esté incompleto. Solicita uno nuevo."
                        : "El enlace no existe o está mal formado."}
              </p>
              <p className="text-xs text-cm-ink-soft mt-4">
                Puedes solicitar uno nuevo desde la pantalla de acceso.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-1 mt-6 text-cm-red font-medium hover:underline text-sm"
                data-testid="reset-back-to-login"
              >
                Volver al inicio <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          )}

          {status === "valid" && !done && (
            <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-form">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <KeyRound className="h-5 w-5 text-cm-red" />
                  <h2 className="font-serif text-xl">Crea tu nueva contraseña</h2>
                </div>
                <p className="text-xs text-cm-ink-soft">
                  NIT <span className="font-mono">{nit}</span>
                  {emailHint && <> · Enviado a <span className="font-mono">{emailHint}</span></>}
                </p>
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium block mb-1.5">
                  Nueva contraseña
                </label>
                <div className="relative">
                  <Input
                    type={showPwd ? "text" : "password"}
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    placeholder="Mínimo 8 caracteres"
                    minLength={8}
                    required
                    data-testid="reset-new-password"
                    className="border-cm-border pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-cm-ink-soft hover:text-cm-ink"
                    tabIndex={-1}
                    aria-label={showPwd ? "Ocultar" : "Mostrar"}
                  >
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {newPwd && newPwd.length < 8 && (
                  <p className="text-xs text-red-600 mt-1">Faltan {8 - newPwd.length} caracteres</p>
                )}
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium block mb-1.5">
                  Confirmar contraseña
                </label>
                <Input
                  type={showPwd ? "text" : "password"}
                  value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  placeholder="Repítela"
                  required
                  data-testid="reset-confirm-password"
                  className="border-cm-border"
                />
                {confirmPwd && newPwd !== confirmPwd && (
                  <p className="text-xs text-red-600 mt-1">Las contraseñas no coinciden</p>
                )}
                {confirmPwd && newPwd === confirmPwd && newPwd.length >= 8 && (
                  <p className="text-xs text-green-700 mt-1 flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Coinciden
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={!valid || submitting}
                className="w-full btn-cm-primary"
                data-testid="reset-submit"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Guardando...</>
                ) : (
                  "Guardar y volver a iniciar sesión"
                )}
              </Button>
            </form>
          )}

          {done && (
            <div className="text-center py-6" data-testid="reset-success">
              <CheckCircle2 className="h-12 w-12 mx-auto text-green-600" />
              <h2 className="font-serif text-xl mt-4">Contraseña actualizada</h2>
              <p className="text-sm text-cm-ink-soft mt-2">
                Te estamos redirigiendo al login...
              </p>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-cm-ink-soft mt-4">
          ¿Necesitas ayuda? WhatsApp a <a href="https://wa.me/573215006806" target="_blank" rel="noopener noreferrer" className="text-cm-red hover:underline">321 5006 806</a>
        </p>
      </div>
    </div>
  );
}
